import Vue from 'vue'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        Component = await Component()
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,

      payload: context.payload,
      error: context.error,
      base: '/static/signup/',
      env: {"VUE_APP_IRONCLAD_SITE_ACCESS_ID":"3652e4b9-a327-430c-8ceb-c8f68a2bbd24","VUE_APP_IRONCLAD_CLICKWRAP_GROUP_KEY_BRAND_AFFILIATE":"embedded-brand-affiliate","VUE_APP_IRONCLAD_CLICKWRAP_GROUP_KEY_MEMBER":"embedded-ns-member","VUE_APP_IRONCLAD_CLICKWRAP_GROUP_KEY_CUSTOMER":"embedded-ns-customer","VUE_APP_EMAIL_DUPLICATION_ENDPOINT":"api.cloud.nuskin.com/email-tools/v1","FF_USE_WINDOWS_LEGACY_PROCESS_STRATEGY":"false","FF_SCRIPT_SECTIONS":"false","FF_POSIXLY_CORRECT_ESCAPES":"false","FF_CONCRETE":"false","CI_JOB_MANUAL":"true","CI_SERVER_VERSION_PATCH":"7","CI_SERVER_REVISION":"9fcb5013c2e","CI_COMMIT_SHORT_SHA":"1906c9d8","CI_COMMIT_TAG_MESSAGE":"","S3_BUCKET_TEST":"nse-test-nuskin-com-static","SAST_EXCLUDED_ANALYZERS":"eslint","GITLAB_USER_LOGIN":"dfloyd","npm_package_resolutions_firebase":"9.9.x","npm_package_devDependencies_identity_obj_proxy":"^3.0.0","npm_package_dependencies__nuskin_ns_util":"4.7.2","npm_package_scripts_dev_mobile":"cross-env APP_ENV=local NUXT_HOST=0.0.0.0 NUXT_PORT=3001 nuxt","CI_DEPENDENCY_PROXY_PASSWORD":"glcbt-67_sFHphm8hzm7UH3gswEYH","CI_DEPENDENCY_PROXY_SERVER":"code.tls.nuskin.io:443","CX_BASE_AUTH_URI":"https://iam.checkmarx.net","npm_package_devDependencies_babel_core":"7.0.0-bridge.0","FF_USE_DUMB_INIT_WITH_KUBERNETES_EXECUTOR":"false","FF_USE_ADAPTIVE_REQUEST_CONCURRENCY":"true","CI_JOB_STARTED_AT_SLUG":"2026-09-25t17-11-05z","SEM_RELEASE_NPM_MODULE":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/SEM_RELEASE_NPM_MODULE","npm_package_resolutions_npm_user_validate":"1.0.1","npm_package_resolutions_minimist":"1.2.6","npm_package_devDependencies_jest_transform_styl":"^1.0.0","npm_package_devDependencies_jest":"^24.0.0","npm_package_devDependencies_eslint_plugin_security":"^1.4.0","npm_package_devDependencies__testing_library_jest_dom":"5.0.2","npm_package_devDependencies__nuxtjs_proxy":"^2.0.1","npm_package_devDependencies__nuxt_types":"^2.14.1","npm_package_dependencies__nuskin_account_components":"1.143.0","npm_config_version_commit_hooks":"true","npm_config_user_agent":"yarn/1.22.19 npm/? node/v16.18.1 linux x64","FF_USE_LEGACY_KUBERNETES_EXECUTION_STRATEGY":"false","FF_LOG_IMAGES_CONFIGURED_FOR_JOB":"false","FF_USE_LEGACY_S3_CACHE_ADAPTER":"false","FF_USE_PARALLEL_ARTIFACT_TRANSFER":"false","CI":"true","CI_SERVER_PROTOCOL":"https","CI_PROJECT_NAME":"signup","NODE_IMAGE":"code.tls.nuskin.io:5005/custom-templates/custom-images:node16.18.1","SEMP_BASIC_AUTH_PRODUCTION":"cHJvZHVjdGlvbi1hZG1pbjpmbmliZm1pZzBoZjZncW9jYTBvM2w0OWQxYQ==","SIGNUP_API_CLIENT_ID":"735b1eb810304bba966af0891ab54053","CI_RUNNER_REVISION":"ac11717a","npm_package_devDependencies__testing_library_user_event":"^13.1.9","npm_package_dependencies_vue":"^2.6.11","npm_package_dependencies_object_path":"0.11.8","npm_package_dependencies__nuskin_app_components":"3.26.0","npm_config_bin_links":"true","HOSTNAME":"runner-nqkmhjcoz-project-1040-concurrent-0","FF_USE_GIT_NATIVE_CLONE":"false","CI_JOB_STAGE":"deploy-to-prod","CI_PROJECT_DESCRIPTION":"","CI_COMMIT_DESCRIPTION":"\n## Summary\nPulls the new Vietnam third-party data-sharing consent block into the signup app by bumping the `@nuskin/account-components` dependency to `1.143.0` (COMX-3605). The consent block itself is implemented in account-components; the signup app renders it at runtime from the deployed component bundle, so this MR is the version bump plus UI-automation updates: ticking the new required consent checkbox, and suppressing the BigID cookie-consent banner that was intercepting clicks.\n\n## Changes\n- `package.json`: `@nuskin/account-components` `1.142.0` -> `1.143.0`.\n- `yarn.lock`: regenerated against the published `1.143.0` (Node 16). `yarn install --frozen-lockfile` passes.\n- `integration/ui/.../pages/SignUpForm.java`: third-party consent locators + VN Member / Brand Affiliate `agreeToTerms` helpers now tick the new required consent box so the signup flow completes.\n- `integration/ui/.../foundation/TestBase.java`: inject a BigID CMP \"consent decided / all-rejected\" cookie (`bigidcmp-consent-<site-id>`) for the test and prod site-ids before each test loads the page, so the cookie-consent banner no longer renders and intercepts clicks during automation. The `consent-id` and timestamps are generated fresh each run.\n\n## Why the version bump releases (semantic-release)\nTagged `New:` to match the account-components release that introduced this feature, so the signup app publishes a minor version carrying `account-components@1.143.0`. The `TestBase` change is `Chore:` (test-only, no release impact).\n\n## Dependencies\n- Requires `@nuskin/account-components@1.143.0` (published - account-components MR merged to master).\n- AEM authoring (tracked on the deploy ticket COMX-3715): new `primary_third_party_consent` form-config field on the VN signup-member and signup-brand-affiliate configs, plus the new/updated `signupPage.*` third-party and privacy string keys in en_VN and vi_VN.\n\nRef: COMX-3605","SEMP_BASIC_AUTH_TESTING":"dGVzdGluZy1hZG1pbjpoNWZkb2cybWhqanM1MmIwZjVzZGdpMDVzcw==","TEST_RAIL_KEY":"d843rftfwcgMxOaPH6wv-VPDEmD.SHAmqBzvSlqXb","SIGNUP_API_CLIENT_SECRET":"0deea37049e84157A406C0C01D29F300","npm_node_execpath":"/usr/local/bin/node","npm_package_resolutions_minimatch":"3.1.2","npm_package_devDependencies_vue_jest":"4.0.0-beta.5","npm_package_dependencies__okta_okta_auth_js":"7.8.1","npm_package_dependencies__nuskin_design_components":"8.8.1","npm_config_init_version":"1.0.0","CI_DEPENDENCY_PROXY_USER":"gitlab-ci-token","CI_SERVER_VERSION":"19.2.7-ee","DOCKER_AUTH_CONFIG":"{\n    \"auths\": {\n        \"registry.shd.nuskin.io\": {\n            \"auth\": \"cm9ib3QtZ2l0bGFiLW9wZXJhdG9yOkwyUlNlVzYzWTBZRHFLRG1wbDQ3QVpCY3VKMk9iSFky\"\n        }\n    }\n}","SEM_RELEASE_FLUTTER_MODULE":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/SEM_RELEASE_FLUTTER_MODULE","DOCKER_REGISTRY_DEPLOY_USERNAME":"docker-registry-deploy-token","FASTLY_API_KEY":"6fvsDuVZYlN0Qq85HfHjqcw6GkCTEC_J","SHLVL":"5","npm_package_resolutions_shell_quote":"1.7.3","npm_package_dependencies_html_webpack_plugin":"^4.3.0","FF_DISABLE_POWERSHELL_STDIN":"false","FF_CLEAN_UP_FAILED_CACHE_EXTRACT":"false","FF_DISABLE_AUTOMATIC_TOKEN_ROTATION":"false","CI_PROJECT_ROOT_NAMESPACE":"ns-am","SPLUNK_ACCESS_TOKEN":"kIy5jbdT_ZYSsqIMecolhw","HOME":"/root","OLDPWD":"/","npm_package_resolutions_ssri":"6.0.2","npm_package_devDependencies_eslint_plugin_babel":"^5.3.0","FF_NETWORK_PER_BUILD":"false","CI_JOB_ID":"3503412","CI_SERVER_HOST":"code.tls.nuskin.io","CI_COMMIT_REF_NAME":"v1.101.0","SECRET_DETECTION_EXCLUDED_PATHS":"coverage","SPLUNK_REALM":"us1","npm_package_resolutions__nuskin_ns_util":"4.x.x","npm_package_devDependencies_eslint_config_prettier":"^4.1.0","FF_RESOLVE_FULL_TLS_CHAIN":"false","CI_PIPELINE_SOURCE":"push","SNS_OPS_TOPIC":"nse-pri-tls-us-west-2-devops-sns","SEM_RELEASE_OPTIONS":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/SEM_RELEASE_OPTIONS","GL_TOKEN":"glpat-1T44YGTjxo9B-GfC7Q74kW86MQp1OjV6MQk.01.0z0mkdkx8","CI_RUNNER_VERSION":"19.4.0","npm_package_resolutions_ip":"2.0.0","npm_package_dependencies_vue_mq":"^1.0.1","npm_package_dependencies_vue_headful":"^2.1.0","npm_package_dependencies_google_auth_library":"6","npm_package_dependencies__nuskin_ns_loyalty_web":"1.5.2","npm_package_scripts_dev_local":"cross-env APP_ENV=local APP_COMP_ENV=local MOCK_API=false nuxt --port 3001","npm_config_init_license":"MIT","FF_USE_FASTZIP":"true","FF_USE_WINDOWS_JOB_OBJECT":"false","CI_BUILDS_DIR":"/gitlab-runner-data/builds","CI_ENVIRONMENT_NAME":"prod","CI_SERVER_VERSION_MAJOR":"19","CI_DEFAULT_BRANCH":"master","CONTENT_TYPE_UID":"application","SONAR_HOST_URL":"https://sonar.nuskin.io","YARN_WRAP_OUTPUT":"false","npm_package_resolutions_ansi_html":"0.0.8","npm_package_devDependencies__nuxtjs_vuetify":"^0.5.0","npm_package_dependencies__nuskin_ns_core_styles":"2.12.2","npm_package_scripts_local_selenium":"echo \"Make sure 'yarn start' is running!\" && cd integration/ui && mvn test-compile surefire:test -Dhost=local -Denvironment=local","npm_config_version_tag_prefix":"v","FF_USE_LEGACY_BASH_EVAL":"false","FF_SUSPENDABLE_ENVIRONMENTS":"false","CI_REGISTRY_PASSWORD":"glcbt-67_sFHphm8hzm7UH3gswEYH","CI_ENVIRONMENT_ACTION":"start","CI_SERVER_URL":"https://code.tls.nuskin.io","CI_TEMPLATE_REGISTRY_HOST":"registry.gitlab.com","CI_COMMIT_REF_PROTECTED":"false","npm_package_devDependencies_jest_extended":"^0.11.5","GITLAB_FEATURES":"audit_events,blocked_issues,blocked_work_items,board_iteration_lists,code_owners,code_review_analytics,full_codequality_report,group_activity_analytics,group_bulk_edit,issuable_default_templates,issue_weights,iterations,ldap_group_sync,merge_request_approvers,milestone_charts,multiple_issue_assignees,multiple_ldap_servers,multiple_merge_request_assignees,multiple_merge_request_reviewers,project_merge_request_analytics,protected_refs_for_users,push_rules,resource_access_token,seat_link,seat_usage_quotas,pipelines_usage_quotas,transfer_usage_quotas,wip_limits,zoekt_code_search,seat_control,usage_billing,group_usage_billing,description_diffs,send_emails_from_admin_area,repository_size_limit,maintenance_mode,scoped_issue_board,contribution_analytics,group_webhooks,member_lock,elastic_search,repository_mirrors,ai_chat,ai_catalog,ai_workflows,ai_analytics,admin_audit_log,agent_managed_resources,agentic_chat,allow_personal_snippets,auditor_user,blocking_merge_requests,board_assignee_lists,board_milestone_lists,ci_job_analytics_for_projects,ci_pipeline_cancellation_restrictions,ci_secrets_management,cluster_agents_ci_impersonation,cluster_agents_user_impersonation,cluster_deployments,code_owner_approval_required,code_suggestions,commit_committer_check,commit_committer_name_check,compliance_framework,configurable_work_item_types,container_virtual_registry,custom_compliance_frameworks,custom_fields,custom_file_templates,custom_project_templates,cycle_analytics_for_groups,cycle_analytics_for_projects,data_management,db_load_balancing,default_branch_protection_restriction_in_groups,default_project_deletion_protection,delete_unconfirmed_users,dependency_firewall,dependency_proxy_for_packages,disable_extensions_marketplace_for_enterprise_users,disable_name_update_for_users,disable_personal_access_tokens,disable_ssh_keys,domain_verification,epic_colors,epics,extended_audit_events,external_authorization_service_api_management,feature_flags_code_references,file_locks,geo,generic_alert_fingerprinting,git_two_factor_enforcement,group_allowed_email_domains,group_ci_cd_analytics_pipelines,group_coverage_reports,group_forking_protection,group_level_analytics_dashboard,group_level_compliance_dashboard,group_milestone_project_releases,group_project_templates,group_repository_analytics,group_saml,group_scoped_ci_variables,ide_schema_config,incident_metric_upload,increased_saved_views_limit,instance_level_scim,jira_issues_integration,ldap_group_sync_filter,linked_items_epics,merge_request_performance_metrics,merge_request_title_regex_check,admin_merge_request_approvers_rules,merge_trains,metrics_reports,mcp_server,multiple_alert_http_integrations,multiple_approval_rules,multiple_group_issue_boards,object_storage,microsoft_group_sync,operations_dashboard,orbit,package_forwarding,packages_virtual_registry,pages_size_limit,pages_multiple_versions,productivity_analytics,project_aliases,project_level_analytics_dashboard,protected_environments,reject_non_dco_commits,reject_unsigned_commits,related_epics,remote_development,saml_group_sync,semantic_code_search,scoped_labels,smartcard_auth,ssh_certificates,swimlanes,target_branch_rules,troubleshoot_job,type_of_work_analytics,minimal_access_role,unprotection_restrictions,ci_project_subscriptions,incident_timeline_view,oncall_schedules,escalation_policies,zentao_issues_integration,coverage_check_approval_rule,issuable_resource_links,group_protected_branches,group_level_merge_checks_setting,oidc_client_groups_claim,disable_deleting_account_for_users,disable_private_profiles,group_saved_replies,requested_changes_block_merge_request,project_saved_replies,default_roles_assignees,ci_component_usages_in_projects,ci_cd_catalog_publish_restriction,branch_rule_squash_options,work_item_status,glab_ask_git_command,generate_commit_message,summarize_new_merge_request,summarize_review,generate_description,summarize_comments,review_merge_request,board_status_lists,disable_invite_members,self_hosted_models,native_secrets_management,built_in_project_templates_enabled,group_ip_restriction,issues_analytics,password_complexity,group_wikis,email_additional_text,custom_file_templates_for_namespace,incident_sla,export_user_permissions,cross_project_pipelines,feature_flags_related_issues,merge_pipelines,ci_cd_projects,github_integration","CI_PROJECT_ID":"1040","CI_REGISTRY_IMAGE":"code.tls.nuskin.io:5005/ns-am/accounts/app/signup","CI_COMMIT_MESSAGE_IS_TRUNCATED":"false","APP_NAME":"signup","npm_package_dependencies__nuskin_ns_product":"^3.35.2","npm_package_dependencies__nuskin_axios_util":"2.3.0","npm_package_scripts_test_updateSnapshots":"jest --updateSnapshot","FF_USE_LEGACY_GCS_CACHE_ADAPTER":"false","GITLAB_CI":"true","CI_COMMIT_SHA":"1906c9d8953f6d2154f88d6b9c4c681c8e0ba98f","CI_COMMIT_TAG":"v1.101.0","SCHEDULED_TARGET_ENVIRONMENT":"test","npm_package_devDependencies_typescript":"3","npm_package_devDependencies_babel_loader":"^8.2.3","npm_package_scripts_test_selenium":"yarn build-dev && start-server-and-test start 3001 local-selenium","npm_package_description":"Signup App","CI_CONCURRENT_ID":"0","NPM_TOKEN":"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","npm_package_readmeFilename":"README.md","npm_package_devDependencies__babel_preset_env":"7.11.0","npm_package_dependencies_nuxt":"^2.14.1","npm_package_scripts_build_dev":"cp ./env/dev.env ./.env && nuxt generate --fail-on-page-error","FF_DISABLE_UMASK_FOR_DOCKER_EXECUTOR":"false","FF_USE_DOCKER_AUTOSCALER_DIAL_STDIO":"true","CI_REGISTRY_USER":"gitlab-ci-token","CI_SERVER_PORT":"443","npm_package_devDependencies_prettier":"^1.16.4","npm_package_devDependencies_jest_silent_reporter":"^0.2.1","npm_package_devDependencies_babel_jest":"^24.0.0","npm_package_devDependencies__babel_plugin_transform_runtime":"^7.11.0","npm_package_dependencies__nuskin_invite_link_service":"2.3.5","npm_package_scripts_build_prod":"cp ./env/prod.env ./.env && nuxt generate --fail-on-page-error","npm_package_scripts_dev":"cross-env APP_ENV=local APP_COMP_ENV=test MOCK_API=false nuxt --port 3001","FF_USE_GIT_PROACTIVE_AUTH":"false","CI_PROJECT_DIR":"/gitlab-runner-data/builds/ns-am/accounts/app/signup","CI_PROJECT_PATH":"ns-am/accounts/app/signup","S3_BUCKET_FEATURE_DEV":"apps.dev.nuskin.io","npm_package_devDependencies_vue_template_compiler":"^2.6.11","npm_package_devDependencies__testing_library_dom":"^7.31.2","npm_package_dependencies_lodash_get":"^4.4.2","FF_ENABLE_JOB_CLEANUP":"false","FF_WAIT_FOR_POD_TO_BE_REACHABLE":"false","CI_PROJECT_NAMESPACE":"ns-am/accounts/app","CI_COMMIT_TIMESTAMP":"2026-09-24T15:32:02-06:00","npm_package_resolutions_handlebars":"4.7.7","npm_package_dependencies_lodash_flatten":"^4.4.0","npm_package_scripts_before_mr":"yarn lint && yarn test && yarn test:selenium","npm_package_scripts_test_debug":"node --inspect ./node_modules/.bin/jest --runInBand --watch","npm_package_private":"true","FF_USE_DIRECT_DOWNLOAD":"true","FF_USE_DYNAMIC_TRACE_FORCE_SEND_INTERVAL":"false","CI_JOB_TOKEN":"glcbt-67_sFHphm8hzm7UH3gswEYH","CI_NODE_TOTAL":"1","CI_SERVER_NAME":"GitLab","CI_CONFIG_REF_URI":"code.tls.nuskin.io/ns-am/accounts/app/signup//.gitlab-ci.yml@refs/tags/v1.101.0","DEV_PORTAL_S3_BUCKET_BASE":"nuskin-ENV-portalcontent-us-west-2-standard","CI_ENVIRONMENT_ID":"306","npm_package_resolutions_json_schema":"0.4.0","npm_package_devDependencies_vuetify_loader":"^1.6.0","npm_package_scripts_lint":"eslint --ext .js,.vue components config layouts pages plugins test utils","npm_config_registry":"https://registry.yarnpkg.com","FF_USE_NATIVE_CONTAINER_STOP":"false","CI_PROJECT_NAMESPACE_ID":"466","CI_PIPELINE_CREATED_AT":"2026-09-24T22:37:36Z","DEV_PORTAL_TOOLS_S3_BUCKET":"nuskin-tls-portalcontent-us-west-2-standard","CX_APIKEY":"eyJhbGciOiJIUzI1NiIsInR5cCIgOiAiSldUIiwia2lkIiA6ICIyM2JmODVjMi02NDZmLTQwMzMtOTE1Yy03YTQ4NDJmMjIwM2UifQ.eyJpYXQiOjE2NjgxMDgzMzEsImp0aSI6ImI0NDA3YjA0LWY1MTctNGE0Yy1iMzI1LWI5NGI2NGVjZGE5NCIsImlzcyI6Imh0dHBzOi8vaWFtLmNoZWNrbWFyeC5uZXQvYXV0aC9yZWFsbXMvbnVza2luIiwiYXVkIjoiaHR0cHM6Ly9pYW0uY2hlY2ttYXJ4Lm5ldC9hdXRoL3JlYWxtcy9udXNraW4iLCJzdWIiOiIyZGM3MTJjOS1lNDZhLTQ5N2EtYTI5ZS0yMDNhMWViZTllYzkiLCJ0eXAiOiJPZmZsaW5lIiwiYXpwIjoiYXN0LWFwcCIsInNlc3Npb25fc3RhdGUiOiJjNWFkNWFmMS02NmI3LTRmNmUtYWFlOS1kYjFmNWY4ZTNhYTciLCJzY29wZSI6IiBvZmZsaW5lX2FjY2VzcyIsInNpZCI6ImM1YWQ1YWYxLTY2YjctNGY2ZS1hYWU5LWRiMWY1ZjhlM2FhNyJ9.gnPSkrjU1K3b6sLhQn2kwsiksqIRBJShlZFb9Vn64DU","CODEARTIFACT_AUTH_TOKEN":"eyJ2ZXIiOjEsImlzdSI6MTc5MDMzNzgwMiwiZW5jIjoiQTEyOEdDTSIsInRhZyI6InhsRG16YkU5X3hMTW9lOEo5cVFiSUEiLCJleHAiOjE3OTAzODEwMDIsImFsZyI6IkExMjhHQ01LVyIsIml2IjoianphdEU3YmxHYXU2UXpjZyJ9.TPYmqa3oK_mX7-25CnQVQg.zz2Ip7WHLxDXyk7Q.1o4QQHOvrwm6qYzbAgw5iUGk4bpDz4EQ95z6dEfr3xKaBytzbVTMKvoc2pCD9MthQ-OHewd3Gtde6ReyOOqn4fdwEAHF_IqHzxTsE60v5mdDzFiFS_fo8WkOucJBA9S_ib1eeHUxviYyG34LlCAVWjCViNexZIblk2mCuTrsGHwGmeAYmPzaJS49lgyz3LjbhkOA7GF2wBhcfDSKNu_hnENml6gMUyhpA41DhcK3Ps6QLmS0kFsfnyIhuQKGrPuICepZEnLp5WzKStktIVWfB6onHAMFSUxrxE5xVnD7sF3vIr3Ta-czRyj8kWpsc5WxEW8biQTRmghlfKwufPVZQyprkH8p6_D8DKkJGO6rJqRkvAtufEUcqnxSYK6V35HjLHcQ8XvrNpbCDIGLrfFm6VTj91ZX0vTGqH9VrTdfc8hmg2MZNeKZsAFDJYp8yLjFslQYF5tbubi2fmri9ynHV40awNcbGTTQMYsFtCc84KLf1pOkP_vPUKMSKiWGFtVrb7PU-pyklBAoH1aMQhJlSbgprRtKsM2n--APfmNEbdUT1HdEz5Qhjz-Tshv1Yzz1Shn_8keNkIjWBGeiZj14t_awWeu_PATtrTZZLn_XmAOwPWKh07nPC3VPGG2j9bFLS_pQ2U3C8WAyYH-Vh-q4nIXp_Y5fqaC94HJCYNavM_qT9kp8xAhFf8qx_VJu9-TFTKkrj8pWHWCJI4_hqDzQyjT8iwWCR3iGmIVyIrdvYsj2EAWqL8xMp0pX6NMRBkrn5pdA4Ij13hlRLeMh6jP9tTfsMT2nX5RGwsUdbZiVtsXG5BSjMaYXGUxAD2lZVwy9rnMZf3u154HWKvkklaXw6e4QBf91j9rrF15304f3N3i0IWIjns85aHM9LbB10z4pec_CMwud06JmmcJtTMCB1W_4iAIJDcOx-LAANmg2PKcUumRVNy2EoV1nVbIKspsqQyXoNcvyH6xyiLH9zI1D_kQ25dpoAmzL0B8w_ftwtJHE8X-Dj80iWPk4d78CmuF0moFiKGMdA029kwJXMN-Iz64oKYVRptslugc_EyIERG8kcMXaIh9rirg3JGACy8tbdQPDWKguNb2pUKBUeh4EYYxsO48hes3LJ0BSDwpr2DP3BMMhBt53ixejJw3sbbe5gv4YyImTgPfsjpyYYLnYuyNkxD4EP8gQTFgCk-ZcOObjkqv_uekE-CJpHQgAkAekmsdLjyaPMKBKnZaQ35VtAp_g3PzaiO-gm0N64c5PpDzl-AmJVD3LOgmGCFgHyvB008Ys43mpgurMFkT48ZU_DRa_9b9RGxq9H3Pyp-fmlptYNWbFCFn06k5TYC-PA3DFZPwiMYLm0GM6f-kSo4jMaUVdY4dFjCOI7V2MARX0cbmPDooAYthU42a28D30eOXdCf7gJWPjmuKgTRYZfHV068ernu5Iy0obAGPEoZXotQ5sq0sy37NDq5D0tJs0oTG1MuUp05nb-NNBphZb8zwZZXuYHX58zwnHd6n0EYivLJdALYQUNwEPgfyX8-If3M_Vn5vT8Gx0sdeRu0q7QqhOAslKqbf2Bsxhm_nncMg0YFx3xC3Sm__pegZncjrgLhViGgfarmOd0RZ5XQIj1vbGNQaR7Vus4zusUy0OfOoa7CAnJeQ6l2Uvyio8vtNs6DsSD0knsPqTd4sXrpu1ZHuyQafwN4doLZRQ_DtHpjLxX3FehRvipLHvWIygoygiaTaN6D0UsWLPae08p50xv3CJImKCQ6b3O-XGBntRhcuALXLSto2vdn2hLNnav3fZb5PVlAqp9oCcsBz_qRDJfx8B5tFClO_kkiHfQ7-4nC89DO_aIoolrwAUo5zrUQ.GlI7kzzTL-JmVxEyHTknQw","npm_package_resolutions_tough_cookie":"4.1.3","npm_package_devDependencies_axios_cache_adapter":"2.7.3","npm_package_devDependencies__babel_core":"^7.11.1","npm_package_dependencies_vuetify":"1","npm_package_dependencies_core_js":"^2.6.11","npm_package_scripts_lintall":"echo \"lintall is deprecated, use lint instead\" && yarn lint","CI_CONCURRENT_PROJECT_ID":"0","CI_JOB_NAME_SLUG":"deploy-to-prod","RUNNER_TEMP_PROJECT_DIR":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp","npm_package_dependencies__nuskin_product_lib":"2.4.1","npm_package_scripts_start":"nuxt start -p 3001","npm_config_ignore_scripts":"","FF_KUBERNETES_HONOR_ENTRYPOINT":"false","CI_PIPELINE_URL":"https://code.tls.nuskin.io/ns-am/accounts/app/signup/-/pipelines/729981","SONAR_IMAGE":"code.tls.nuskin.io:5005/custom-templates/custom-images:sonar-cached-RN-android-31-jdk17-node18","S3_BUCKET_DEV":"nse-dev-nuskin-com-static","NSE_TLS_ACCOUNT_ID":"537237850156","CI_RELEASE_NAME":"v1.101.0","npm_package_resolutions__vue_component_compiler_utils":"^3.0.0","PATH":"/tmp/yarn--1790356317677-0.5894337190466292:/gitlab-runner-data/builds/ns-am/accounts/app/signup/node_modules/.bin:/usr/local/share/.config/yarn/link/node_modules/.bin:/usr/local/libexec/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/lib/node_modules/npm/bin/node-gyp-bin:/usr/local/bin/node_modules/npm/bin/node-gyp-bin:/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin","NODE":"/usr/local/bin/node","FF_EXPORT_HIGH_CARDINALITY_METRICS":"false","FF_ENABLE_JOB_INPUTS_INTERPOLATION":"true","CI_JOB_STARTED_AT":"2026-09-25T17:11:05Z","CI_SERVER_VERSION_MINOR":"2","CI_RUNNER_DESCRIPTION":"nse-prd-us-west-2-runner-shared-i-020cfb12c00f72c8f","CI_RELEASE_DESCRIPTION":"# [1.101.0](https://code.tls.nuskin.io/ns-am/accounts/app/signup/compare/v1.100.0...v1.101.0) (2026-09-24)\n\n\n\n","npm_package_dependencies__nuskin_ns_checkout_common":"^3.34.42","npm_package_name":"signup-app","FF_DISABLE_UMASK_FOR_KUBERNETES_EXECUTOR":"false","FF_MASK_ALL_DEFAULT_TOKENS":"true","CI_PROJECT_TITLE":"Signup","CI_PROJECT_VISIBILITY":"private","CI_COMMIT_TITLE":"New: Add VN third-party data-sharing consent via account-components 1.143.0 (#COMX-3605)","KUBECONFIG":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/KUBECONFIG","GITLAB_USER_EMAIL":"dfloyd@nuskin.com","npm_package_resolutions_serialize_javascript":"3.1.0","npm_package_dependencies__nuskin_product_components":"^2.16.0","FF_USE_GIT_BUNDLE_URIS":"true","FF_USE_FLEETING_ACQUIRE_HEARTBEATS":"false","CI_SERVER":"yes","CI_JOB_GROUP_NAME":"deploy-to-prod","npm_package_resolutions_express":"4.17.3","npm_package_resolutions_parse_url":"8.1.0","FF_TIMESTAMPS":"true","FF_MACHINE_OPTIONS_EXPAND_ENV":"false","CI_PROJECT_TOPICS":"","CI_PROJECT_REPOSITORY_LANGUAGES":"java,javascript,vue,scss,shell","DOCKER_REGISTRY_DEPLOY_PASSWORD":"gldt-GgrCR7NqVCRP-3Y5qHDr","SEMP_BASIC_AUTH_DEVELOPMENT":"ZGV2ZWxvcG1lbnQtYWRtaW46ODU0czYyNXV1ams3aGE2MWxzNHViY2RjM2o=","CI_PAGES_URL":"https://ns-am.pages.nuskin.io/accounts/app/signup","npm_package_devDependencies_webpack":"^4.44.1","npm_package_devDependencies_msw":"^0.20.5","npm_package_devDependencies_eslint_plugin_json":"2.0.1","npm_package_devDependencies_eslint":"^5.16.0","npm_package_devDependencies__vue_test_utils":"^1.0.3","FF_PRINT_POD_EVENTS":"false","FF_USE_EXPONENTIAL_BACKOFF_STAGE_RETRY":"true","FF_USE_JOB_ROUTER":"false","CI_SERVER_FQDN":"code.tls.nuskin.io","CI_COMMIT_AUTHOR":"Daniel Floyd <dfloyd@nuskin.com>","SONAR_URL":"https://sonar.nuskin.io","HARBOR_REGISTRY":"registry.shd.nuskin.io","CS_TOKEN":"cs7c1b52de0a69f5da43cac376","CX_TENANT":"nuskin","npm_lifecycle_script":"cp ./env/prod.env ./.env && nuxt generate --fail-on-page-error","npm_package_dependencies_lodash_debounce":"^4.0.8","npm_package_scripts_build_test":"cp ./env/test.env ./.env && nuxt generate --fail-on-page-error","FF_USE_PARALLEL_CACHE_TRANSFER":"false","CI_JOB_IMAGE":"code.tls.nuskin.io:5005/custom-templates/custom-images:node16.18.1","CI_RUNNER_SHORT_TOKEN":"NQkMhjCoz","CI_PROJECT_ROOT_NAMESPACE_SLUG":"ns-am","CI_PAGES_DOMAIN":"pages.nuskin.io","CI_TRACEPARENT":"00-000000000000000000000000000b237d-9342eb6156329983-01","DEPLOY_SCRATCH_ON_EVERY_COMMIT":"true","npm_package_devDependencies_jest_localstorage_mock":"^2.4.3","npm_package_devDependencies__babel_plugin_proposal_nullish_coalescing_operator":"^7.10.4","npm_package_dependencies__nuskin_http_client":"^3.0.2","npm_package_scripts_test":"jest","npm_config_version_git_message":"v%s","CI_JOB_TIMEOUT":"3600","CI_REPOSITORY_URL":"https://gitlab-ci-token:glcbt-67_sFHphm8hzm7UH3gswEYH@code.tls.nuskin.io/ns-am/accounts/app/signup.git","CI_PROJECT_CLASSIFICATION_LABEL":"","CI_PIPELINE_NAME":"","SPLUNK_LAYER_ARN":"arn:aws:lambda:us-west-2:254067382080:layer:splunk-apm:116","GITLAB_ENV":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/gitlab_runner_env","npm_lifecycle_event":"build-prod","npm_package_resolutions_axios":"0.27.2","npm_package_devDependencies_jest_transform_stub":"^2.0.0","npm_package_devDependencies_jest_transform_css":"^2.0.0","npm_package_dependencies__nuxtjs_dotenv":"^1.4.1","npm_package_dependencies__nuskin_ns_product_lib":"2.20.0","npm_package_dependencies__nuskin_ns_feature_flags_vue":"^2.0.2","npm_package_version":"1.0.0","FF_USE_POWERSHELL_PATH_RESOLVER":"false","FF_GIT_URLS_WITHOUT_TOKENS":"false","CI_ENVIRONMENT_TIER":"production","CI_API_GRAPHQL_URL":"https://code.tls.nuskin.io/api/graphql","CI_RUNNER_ID":"44077","S3_BUCKET_PROD":"nse-www-nuskin-com-static","npm_config_argv":"{\"remain\":[],\"cooked\":[\"run\",\"build-prod\"],\"original\":[\"build-prod\"]}","npm_package_dependencies_vue_i18n":"^8.21.0","npm_package_dependencies_lodash_merge":"^4.6.2","npm_package_dependencies__nuskin_ns_market_config_vue":"2.0.0","npm_package_scripts_build":"nuxt build","npm_package_scripts_dev_mock":"cross-env APP_ENV=local MOCK_API=true nuxt --port 3001","npm_config_unsafe_perm":"true","CI_DEFAULT_BRANCH_SLUG":"master","CI_REGISTRY":"code.tls.nuskin.io:5005","CI_DEPENDENCY_PROXY_DIRECT_GROUP_IMAGE_PREFIX":"code.tls.nuskin.io:443/ns-am/accounts/app/dependency_proxy/containers","CI_API_V4_URL":"https://code.tls.nuskin.io/api/v4","MAVEN_IMAGE":"maven:3.9.10-amazoncorretto-11","GITLAB_USER_NAME":"Daniel Floyd","SNS_API_KEY":"f46a3d310059459d869625293db0bd40","DEV_PORTAL_S3_BUCKET":"nuskin-dev-portalcontent-us-west-2-standard-dev","CX_BASE_URI":"https://ast.checkmarx.net","npm_package_devDependencies_portal_vue":"^2.1.7","CI_PIPELINE_IID":"3828","npm_package_dependencies__nuskin_ns_account":"5.11.1","npm_package_scripts_load_test":"echo \"Load testing AWS S3 is silly\"","npm_config_version_git_tag":"true","npm_config_version_git_sign":"","FF_USE_POD_ACTIVE_DEADLINE_SECONDS":"true","FF_USE_ADVANCED_POD_SPEC_CONFIGURATION":"false","CI_JOB_URL":"https://code.tls.nuskin.io/ns-am/accounts/app/signup/-/jobs/3503412","CI_SERVER_SHELL_SSH_HOST":"code.tls.nuskin.io","CI_TRACESTATE":"gitlab=pipeline:729981;job:3503412","CI_RUNNER_EXECUTABLE_ARCH":"linux/amd64","npm_package_devDependencies_start_server_and_test":"^1.11.3","npm_package_dependencies_sass":"^1.26.10","npm_package_scripts_generate":"nuxt generate --fail-on-page-error","npm_config_strict_ssl":"true","FF_TEST_FEATURE":"false","FF_USE_INIT_WITH_DOCKER_EXECUTOR":"false","IS_CONTINUOUS_DEPLOYMENT":"true","SALESFORCE_DEPLOYMENT_APPROACH":"ORG_BASED","npm_package_resolutions_normalize_url":"4.5.1","npm_package_devDependencies_eslint_plugin_prettier":"^3.0.1","npm_package_scripts_lintfix":"eslint --ext .js,.vue components config layouts pages plugins test utils --fix","npm_package_scripts_test_watch":"jest --watch","FF_SCRIPT_TO_STEP_MIGRATION":"false","CI_COMMIT_REF_SLUG":"v1-101-0","PROJECT_LANGUAGE":"JavaScript","CLIENT_APP_TYPE":"static","CI_DISPOSABLE_ENVIRONMENT":"true","PWD":"/gitlab-runner-data/builds/ns-am/accounts/app/signup","npm_execpath":"/opt/yarn-v1.22.19/bin/yarn.js","npm_package_dependencies__nuskin_ns_signup_api_client":"^2.12.1","FF_SECRET_RESOLVING_FAILS_IF_MISSING":"true","FF_USE_GITALY_CORRELATION_ID":"true","CI_RUNNER_TAGS":"[\"docker\", \"us-west-2\", \"shared-runner\", \"nse-prd\", \"amd64\", \"linux\", \"gp3\", \"c5d.4xlarge\"]","npm_package_resolutions_vue_loader":"15.9.3","npm_package_devDependencies_jest_css_modules":"^2.1.0","npm_package_devDependencies_flush_promises":"^1.0.2","npm_package_author_name":"mcassidy","CI_SERVER_TLS_CA_FILE":"/gitlab-runner-data/builds/ns-am/accounts/app/signup.tmp/CI_SERVER_TLS_CA_FILE","CI_PIPELINE_ID":"729981","CI_PROJECT_PATH_SLUG":"ns-am-accounts-app-signup","CI_PROJECT_URL":"https://code.tls.nuskin.io/ns-am/accounts/app/signup","CI_CONFIG_PATH":".gitlab-ci.yml","CI_COMMIT_BEFORE_SHA":"0000000000000000000000000000000000000000","CI_ENVIRONMENT_SLUG":"prod","npm_package_devDependencies_postcss":"7","npm_package_devDependencies_eslint_plugin_vue":"^5.2.3","npm_package_devDependencies_cross_env":"^7.0.2","npm_package_devDependencies__babel_plugin_proposal_optional_chaining":"^7.11.0","npm_package_dependencies__nuskin_ns_shop":"4.1.1","npm_package_scripts_test_silent":"jest --reporters jest-silent-reporter --collectCoverage=false","npm_config_save_prefix":"^","npm_config_ignore_optional":"","FF_HASH_CACHE_KEYS":"false","CI_PROJECT_NAMESPACE_SLUG":"ns-am-accounts-app","CI_COMMIT_MESSAGE":"New: Add VN third-party data-sharing consent via account-components 1.143.0 (#COMX-3605)\n\n## Summary\nPulls the new Vietnam third-party data-sharing consent block into the signup app by bumping the `@nuskin/account-components` dependency to `1.143.0` (COMX-3605). The consent block itself is implemented in account-components; the signup app renders it at runtime from the deployed component bundle, so this MR is the version bump plus UI-automation updates: ticking the new required consent checkbox, and suppressing the BigID cookie-consent banner that was intercepting clicks.\n\n## Changes\n- `package.json`: `@nuskin/account-components` `1.142.0` -> `1.143.0`.\n- `yarn.lock`: regenerated against the published `1.143.0` (Node 16). `yarn install --frozen-lockfile` passes.\n- `integration/ui/.../pages/SignUpForm.java`: third-party consent locators + VN Member / Brand Affiliate `agreeToTerms` helpers now tick the new required consent box so the signup flow completes.\n- `integration/ui/.../foundation/TestBase.java`: inject a BigID CMP \"consent decided / all-rejected\" cookie (`bigidcmp-consent-<site-id>`) for the test and prod site-ids before each test loads the page, so the cookie-consent banner no longer renders and intercepts clicks during automation. The `consent-id` and timestamps are generated fresh each run.\n\n## Why the version bump releases (semantic-release)\nTagged `New:` to match the account-components release that introduced this feature, so the signup app publishes a minor version carrying `account-components@1.143.0`. The `TestBase` change is `Chore:` (test-only, no release impact).\n\n## Dependencies\n- Requires `@nuskin/account-components@1.143.0` (published - account-components MR merged to master).\n- AEM authoring (tracked on the deploy ticket COMX-3715): new `primary_third_party_consent` form-config field on the VN signup-member and signup-brand-affiliate configs, plus the new/updated `signupPage.*` third-party and privacy string keys in en_VN and vi_VN.\n\nRef: COMX-3605\n","NEXUS3_AUTH":"Z2l0bGFiOjNaNVdKTk1mek1hNg==","npm_package_resolutions_execa":"2.0.0","npm_package_resolutions_lodash":"4.17.21","npm_package_devDependencies_babel_eslint":"^10.1.0","npm_package_dependencies_vue2_transitions":"^0.3.0","npm_package_dependencies__nuxtjs_axios":"^5.12.1","FF_ENABLE_BASH_EXIT_CODE_CHECK":"false","CI_PAGES_HOSTNAME":"ns-am.pages.nuskin.io","CI_DEPENDENCY_PROXY_GROUP_IMAGE_PREFIX":"code.tls.nuskin.io:443/ns-am/dependency_proxy/containers","CI_COMMIT_USER_LOGIN":"","GITLAB_USER_ID":"464","NEXUS3_TOKEN":"a4c7394b-8d07-3634-8020-e9ac03ce123f","npm_package_dependencies__nuskin_configuration_sdk":"3.0.1","INIT_CWD":"/gitlab-runner-data/builds/ns-am/accounts/app/signup","CI_JOB_STATUS":"running","CI_JOB_NAME":"deploy-to-prod","CI_SERVER_SHELL_SSH_PORT":"22","npm_package_resolutions_sass":">=1.23.0 <1.26.0","npm_package_devDependencies_eslint_config_google":"^0.14.0","npm_package_devDependencies__peculiar_webcrypto":"^1.4.3","npm_package_dependencies__nuskin_ns_gtm":"^1.0.0","NODE_ENV":"production","_applied":"true"}
    }
    // Only set once
    if (!process.static && context.req) {
      app.context.req = context.req
    }
    if (!process.static && context.res) {
      app.context.res = context.res
    }
    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = formatUrl(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  let path = decodeURI(window.location.pathname)
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }
  // To get matched with sanitized router.base add trailing slash
  if (base && (path.endsWith('/') ? path : path + '/').startsWith(base)) {
    path = path.slice(base.length)
  }
  return (path || '/') + window.location.search + window.location.hash
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

/**
 * Format given url, append query to url query string
 *
 * @param  {string} url
 * @param  {string} query
 * @return {string}
 */
function formatUrl (url, query) {
  let protocol
  const index = url.indexOf('://')
  if (index !== -1) {
    protocol = url.substring(0, index)
    url = url.substring(index + 3)
  } else if (url.startsWith('//')) {
    url = url.substring(2)
  }

  let parts = url.split('/')
  let result = (protocol ? protocol + '://' : '//') + parts.shift()

  let path = parts.join('/')
  if (path === '' && parts.length === 1) {
    result += '/'
  }

  let hash
  parts = path.split('#')
  if (parts.length === 2) {
    [path, hash] = parts
  }

  result += path ? '/' + path : ''

  if (query && JSON.stringify(query) !== '{}') {
    result += (url.split('?').length === 2 ? '&' : '?') + formatQuery(query)
  }
  result += hash ? '#' + hash : ''

  return result
}

/**
 * Transform data object to query string
 *
 * @param  {object} query
 * @return {string}
 */
function formatQuery (query) {
  return Object.keys(query).sort().map((key) => {
    const val = query[key]
    if (val == null) {
      return ''
    }
    if (Array.isArray(val)) {
      return val.slice().map(val2 => [key, '=', val2].join('')).join('&')
    }
    return key + '=' + val
  }).filter(Boolean).join('&')
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export function urlJoin () {
  return [].slice
    .call(arguments)
    .join('/')
    .replace(/\/+/g, '/')
    .replace(':/', '://')
}

export function stripTrailingSlash (path) {
  return path.replace(/\/+$/, '') || '/'
}

export function isSamePath (p1, p2) {
  return stripTrailingSlash(p1) === stripTrailingSlash(p2)
}
